import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, useReducedMotion, AnimatePresence } from 'framer-motion';
import { useTheme } from '@mui/material';
import type { SpotlightStepConfig } from '../../types';

interface TargetRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface SpotlightOverlayProps {
  step: SpotlightStepConfig;
  onClickOutside: () => void;
}

const CUTOUT_PADDING = 8;
const CUTOUT_BORDER_RADIUS = 12;

function findTarget(step: SpotlightStepConfig): Element | null {
  return (
    document.querySelector(step.targetSelector) ||
    (step.targetFallback ? document.querySelector(step.targetFallback) : null)
  );
}

function getTargetRect(el: Element): TargetRect {
  const rect = el.getBoundingClientRect();
  return {
    top: rect.top - CUTOUT_PADDING,
    left: rect.left - CUTOUT_PADDING,
    width: rect.width + CUTOUT_PADDING * 2,
    height: rect.height + CUTOUT_PADDING * 2,
  };
}

const SpotlightOverlay: React.FC<SpotlightOverlayProps> = ({ step, onClickOutside }) => {
  const theme = useTheme();
  const shouldReduceMotion = useReducedMotion();
  const [targetRect, setTargetRect] = useState<TargetRect | null>(null);
  const resizeRaf = useRef<number | null>(null);

  const updateRect = useCallback(() => {
    const el = findTarget(step);
    if (!el) return;

    // Scroll into view if off-screen
    const rect = el.getBoundingClientRect();
    const isOffScreen =
      rect.bottom < 0 ||
      rect.top > window.innerHeight ||
      rect.right < 0 ||
      rect.left > window.innerWidth;

    if (isOffScreen) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Recalculate after scroll settles
      setTimeout(() => {
        setTargetRect(getTargetRect(el));
      }, 400);
    } else {
      setTargetRect(getTargetRect(el));
    }
  }, [step]);

  // Initial positioning and resize handler
  useEffect(() => {
    updateRect();

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    const handleResize = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        if (resizeRaf.current) cancelAnimationFrame(resizeRaf.current);
        resizeRaf.current = requestAnimationFrame(updateRect);
      }, 100);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize, { passive: true });

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize);
      if (debounceTimer) clearTimeout(debounceTimer);
      if (resizeRaf.current) cancelAnimationFrame(resizeRaf.current);
    };
  }, [updateRect]);

  // Recalculate when step changes
  useEffect(() => {
    updateRect();
  }, [step.targetSelector, updateRect]);

  if (!targetRect) return null;

  const teal = theme.palette.secondary.main;

  return (
    <AnimatePresence>
      {/* Full-screen click catcher (behind the cutout) */}
      <motion.div
        key="overlay-backdrop"
        initial={shouldReduceMotion ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={shouldReduceMotion ? undefined : { opacity: 0, transition: { duration: 0.2 } }}
        transition={shouldReduceMotion ? { duration: 0 } : { duration: 0.3, ease: 'easeOut' }}
        onClick={onClickOutside}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 1449,
          cursor: 'default',
        }}
      />

      {/* Cutout highlight around target */}
      <motion.div
        key="overlay-cutout"
        initial={shouldReduceMotion ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={shouldReduceMotion ? undefined : { opacity: 0, transition: { duration: 0.2 } }}
        transition={shouldReduceMotion ? { duration: 0 } : { duration: 0.3, ease: 'easeOut' }}
        style={{
          position: 'fixed',
          top: targetRect.top,
          left: targetRect.left,
          width: targetRect.width,
          height: targetRect.height,
          zIndex: 1450,
          borderRadius: CUTOUT_BORDER_RADIUS,
          boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.4)',
          pointerEvents: 'none',
        }}
      >
        {/* Pulsing border glow */}
        <motion.div
          animate={
            shouldReduceMotion
              ? { borderColor: `${teal}` }
              : {
                  borderColor: [`${teal}66`, `${teal}`, `${teal}66`],
                }
          }
          transition={
            shouldReduceMotion
              ? { duration: 0 }
              : {
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }
          }
          style={{
            position: 'absolute',
            inset: -2,
            borderRadius: CUTOUT_BORDER_RADIUS + 2,
            border: `2px solid ${teal}`,
            pointerEvents: 'none',
          }}
        />
      </motion.div>
    </AnimatePresence>
  );
};

export default SpotlightOverlay;
